/**
 * MessageBubble Component - Individual Message Display
 *
 * Renders a single message with appropriate styling based on ownership.
 * Supports different message types (text, system, proposal) and states
 * (pending, deleted).
 *
 * Features:
 * - Own messages: Right-aligned, blue background
 * - Other messages: Left-aligned, gray background with avatar
 * - AI messages: Special styling with artifact chip support
 * - Artifact chip parsing: [[ARTIFACT:TYPE:ID:LABEL]] tokens → clickable chips
 * - Markdown rendering: **bold**, line breaks
 * - Timestamps: Relative time display
 * - Status indicators: Pending, sent, failed
 * - Anonymous sender resolution: Fetches user profile when senderName is "Anonymous"
 */

import React, { useState, useEffect } from 'react';
import { Box, Typography, Avatar, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  AccessTime as AccessTimeIcon,
  SmartToy as SmartToyIcon,
  AccountTree as AccountTreeIcon,
  Hotel as HotelIcon,
  Place as PlaceIcon,
  Flight as FlightIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { formatRelativeTime } from '../../utils/timeUtils';
import { getUserProfile } from '@trip-planner-monorepo/shared';
import './MessageBubble.css';

// ---------------------------------------------------------------------------
// ArtifactChip helpers (inline - mirrors travel-agent/ArtifactChip.jsx)
// ---------------------------------------------------------------------------

function getArtifactIcon(type) {
  switch (type) {
    case 'TRIP_TIMELINE': return <AccountTreeIcon sx={{ fontSize: '0.875rem' }} />;
    case 'ACCOMMODATION':
    case 'ENTITY_VIEW_accommodation': return <HotelIcon sx={{ fontSize: '0.875rem' }} />;
    case 'PLACE':
    case 'ENTITY_VIEW_place': return <PlaceIcon sx={{ fontSize: '0.875rem' }} />;
    case 'TRAVEL':
    case 'ENTITY_VIEW_travel': return <FlightIcon sx={{ fontSize: '0.875rem' }} />;
    case 'DAY':
    case 'ENTITY_VIEW_day': return <CalendarIcon sx={{ fontSize: '0.875rem' }} />;
    default: return <AccountTreeIcon sx={{ fontSize: '0.875rem' }} />;
  }
}

function getArtifactColor(type) {
  switch (type) {
    case 'TRIP_TIMELINE': return 'primary';
    case 'ACCOMMODATION':
    case 'ENTITY_VIEW_accommodation': return 'secondary';
    case 'PLACE':
    case 'ENTITY_VIEW_place': return 'success';
    case 'TRAVEL':
    case 'ENTITY_VIEW_travel': return 'info';
    default: return 'default';
  }
}

/**
 * Build the navigation path for an artifact type + id.
 */
function getArtifactPath(type, id) {
  switch (type) {
    case 'TRIP_TIMELINE': return null; // no direct nav path yet
    case 'ENTITY_VIEW_accommodation':
    case 'ACCOMMODATION': return `/accommodation/${id}`;
    case 'ENTITY_VIEW_place':
    case 'PLACE': return `/place/${id}`;
    case 'ENTITY_VIEW_travel':
    case 'TRAVEL': return `/travel/${id}`;
    case 'ENTITY_VIEW_day':
    case 'DAY': return `/day/${id}`;
    default: return null;
  }
}

/**
 * Inline ArtifactChip — renders a navigable Chip for a trip entity reference.
 */
const ArtifactChip = ({ type, id, label, onNavigate }) => {
  const handleClick = () => {
    if (onNavigate) onNavigate(type, id, label);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <Chip
      icon={getArtifactIcon(type)}
      label={label || type}
      size="small"
      color={getArtifactColor(type)}
      variant="outlined"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      sx={{
        cursor: 'pointer',
        fontSize: '0.75rem',
        height: '22px',
        mx: 0.25,
        verticalAlign: 'middle',
        '&:hover': { opacity: 0.8 }
      }}
      title={`View ${label || type}`}
    />
  );
};

// ---------------------------------------------------------------------------
// Text parsing helpers
// ---------------------------------------------------------------------------

const ARTIFACT_PATTERN = /\[\[ARTIFACT:([^:\]]+):([^:\]]+):([^\]]+)\]\]/g;

/**
 * Parse text containing [[ARTIFACT:TYPE:ID:LABEL]] tokens.
 * Returns an array of strings and ArtifactChip React elements.
 */
function parseArtifacts(text, onNavigate) {
  if (!text) return [''];

  const parts = [];
  let lastIndex = 0;
  let match;
  const pattern = new RegExp(ARTIFACT_PATTERN.source, 'g');

  while ((match = pattern.exec(text)) !== null) {
    const [fullMatch, type, id, label] = match;

    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    parts.push(
      <ArtifactChip
        key={`artifact-${id}-${match.index}`}
        type={type}
        id={id}
        label={label}
        onNavigate={onNavigate}
      />
    );

    lastIndex = match.index + fullMatch.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

/**
 * Render a text segment with basic markdown: **bold** and newlines.
 */
function renderTextWithMarkdown(text, keyBase = 0) {
  if (!text) return [];

  const lines = text.split('\n');
  const result = [];

  lines.forEach((line, lineIndex) => {
    if (lineIndex > 0) {
      result.push(<br key={`br-${keyBase}-${lineIndex}`} />);
    }

    const boldPattern = /\*\*([^*]+)\*\*/g;
    const lineParts = [];
    let lastIdx = 0;
    let boldMatch;

    while ((boldMatch = boldPattern.exec(line)) !== null) {
      if (boldMatch.index > lastIdx) {
        lineParts.push(line.slice(lastIdx, boldMatch.index));
      }
      lineParts.push(
        <strong key={`bold-${keyBase}-${lineIndex}-${boldMatch.index}`}>
          {boldMatch[1]}
        </strong>
      );
      lastIdx = boldMatch.index + boldMatch[0].length;
    }

    if (lastIdx < line.length) {
      lineParts.push(line.slice(lastIdx));
    }

    result.push(...lineParts);
  });

  return result;
}

/**
 * Render message content with artifact chips and markdown formatting.
 */
function renderContent(text, onNavigate) {
  const parts = parseArtifacts(text, onNavigate);

  return parts.map((part, index) => {
    if (typeof part === 'string') {
      return (
        <React.Fragment key={`text-${index}`}>
          {renderTextWithMarkdown(part, index)}
        </React.Fragment>
      );
    }
    // Already a React element (ArtifactChip)
    return part;
  });
}

// ---------------------------------------------------------------------------
// MessageBubble Component
// ---------------------------------------------------------------------------

/**
 * MessageBubble Component
 *
 * @param {Object} props
 * @param {Object} props.message - Message data
 * @param {boolean} props.isOwn - Whether message is from current user
 */
const MessageBubble = ({ message, isOwn }) => {
  const navigate = useNavigate();

  const {
    text,
    content,
    senderName,
    senderId,
    senderAvatar,
    timestamp,
    type = 'text',
    isDeleted = false,
    isPending = false
  } = message;
  const displayText = text ?? content ?? '';

  // State for resolved sender name (handles Anonymous fallback)
  const [resolvedSenderName, setResolvedSenderName] = useState(senderName);

  // Fetch user profile if senderName is "Anonymous"
  useEffect(() => {
    const fetchSenderProfile = async () => {
      if (senderId === 'AI_AGENT') return;

      if (senderName === 'Anonymous' && senderId) {
        try {
          const userProfile = await getUserProfile(senderId);
          if (userProfile) {
            const displayName = userProfile.displayName ||
              (userProfile.email ? `${userProfile.email.split('@')[0]}@...` : null);
            if (displayName) {
              setResolvedSenderName(displayName);
            }
          }
        } catch (error) {
          console.error('[MessageBubble] Error fetching user profile:', error);
        }
      }
    };

    fetchSenderProfile();
  }, [senderName, senderId]);

  /**
   * Handle artifact chip navigation.
   * Navigates to the entity's detail page using React Router.
   */
  const handleArtifactNavigate = (artifactType, artifactId, artifactLabel) => {
    const path = getArtifactPath(artifactType, artifactId);
    if (path) {
      navigate(path);
    } else {
      console.warn('[MessageBubble] No navigation path for artifact type:', artifactType);
    }
  };

  // Detect AI messages
  const isAI = senderId === 'AI_AGENT';

  // AI messages - Special styling with artifact support
  if (isAI) {
    return (
      <Box className="message-bubble ai-message">
        <Box className="message-header">
          <Avatar className="ai-avatar">
            <SmartToyIcon />
          </Avatar>
          <Typography variant="caption" className="sender-name">
            {senderName}
          </Typography>
          <Chip label="Gemini" size="small" className="ai-model-badge" />
        </Box>
        <Box className="message-content">
          <Typography variant="body1" component="div" sx={{ lineHeight: 1.6 }}>
            {renderContent(displayText, handleArtifactNavigate)}
          </Typography>
        </Box>
        <Typography variant="caption" className="timestamp">
          {formatRelativeTime(timestamp)}
        </Typography>
        {message.metadata?.toolsUsed?.length > 0 && (
          <Box className="tools-used">
            <Typography variant="caption">
              Used: {message.metadata.toolsUsed.join(', ')}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // System messages
  if (type === 'system') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          margin: '12px 0'
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontStyle: 'italic',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            padding: '4px 12px',
            borderRadius: '12px'
          }}
        >
          {displayText}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isOwn ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        marginBottom: '12px',
        gap: 1
      }}
    >
      {/* Avatar (only for other users) */}
      {!isOwn && (
        <Avatar
          src={senderAvatar}
          alt={resolvedSenderName}
          sx={{ width: 32, height: 32, fontSize: '0.875rem' }}
        >
          {resolvedSenderName?.charAt(0)?.toUpperCase() || '?'}
        </Avatar>
      )}

      {/* Message Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '70%',
          minWidth: '100px'
        }}
      >
        {/* Sender Name (only for other users) */}
        {!isOwn && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              marginBottom: '4px',
              marginLeft: '12px',
              fontWeight: 500
            }}
          >
            {resolvedSenderName}
          </Typography>
        )}

        {/* Message Bubble */}
        <Box
          sx={{
            backgroundColor: isOwn
              ? type === 'proposal' ? '#1565c0' : '#1976d2'
              : type === 'proposal' ? '#e3f2fd' : '#f5f5f5',
            color: isOwn ? '#fff' : '#000',
            padding: '10px 14px',
            borderRadius: isOwn ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
            wordBreak: 'break-word',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
            opacity: isPending ? 0.7 : 1
          }}
        >
          {/* Message Text */}
          <Typography
            variant="body2"
            component="div"
            sx={{
              color: 'inherit',
              fontSize: '0.9375rem',
              lineHeight: 1.5,
              fontStyle: isDeleted ? 'italic' : 'normal',
              opacity: isDeleted ? 0.6 : 1
            }}
          >
            {isDeleted ? '[Message deleted]' : displayText}
          </Typography>

          {/* Timestamp and Status */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              marginTop: '4px',
              gap: 0.5
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: isOwn ? 'rgba(255, 255, 255, 0.8)' : 'text.secondary',
                fontSize: '0.75rem'
              }}
            >
              {formatRelativeTime(timestamp)}
            </Typography>

            {isPending && (
              <AccessTimeIcon
                sx={{
                  fontSize: '0.875rem',
                  color: isOwn ? 'rgba(255, 255, 255, 0.8)' : 'text.secondary'
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MessageBubble;
